/**
 * Compliance Gap Panel Component
 *
 * DC-693: Displays organization-level gap between governance policy (documents)
 * and actual practice (datasets).
 *
 * Design: Documents establish the benchmark (MAX per pillar = highest claimed maturity).
 * Datasets show actual practice (AVG per pillar).
 * Gap = Practice - Benchmark (negative = underperforming).
 *
 * @see Documentation/Development/Design Docs/Missing_Accordion_Capabilities_Design.md
 */

import React, { useState, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  Link2,
  LockOpen,
  Recycle,
  Search
} from 'lucide-react';
import './compliance-gap-panel.css';

// Types matching server/compliance/organizationalComplianceGap.ts
type Pillar = 'F' | 'A' | 'I' | 'R';

interface PillarBenchmark {
  pillar: Pillar;
  pillarName: string;
  score: number;
  sourceDocument: string;
  documentCount: number;
}

interface PillarPractice {
  pillar: Pillar;
  pillarName: string;
  score: number;
  datasetCount: number;
  belowBenchmark: number;
}

interface DatasetBelowBar {
  journeyId: string;
  journeyName: string;
  pillarScore: number;
  benchmarkScore: number;
  gap: number;
}

interface ComplianceGap {
  pillar: Pillar;
  pillarName: string;
  benchmark: number;
  practice: number;
  gap: number;
  gapPercentage: number;
  status: 'exceeds' | 'on_track' | 'gap' | 'critical_gap';
  datasetsBelowBar: DatasetBelowBar[];
}

interface ComplianceReport {
  analyzedAt: string;
  documentsAnalyzed: number;
  datasetsAnalyzed: number;
  policyBenchmark: Record<Pillar, PillarBenchmark>;
  actualPractice: Record<Pillar, PillarPractice>;
  gaps: ComplianceGap[];
  overallComplianceScore: number;
  summary: {
    exceeds: number;
    onTrack: number;
    gap: number;
    criticalGap: number;
  };
}

const PILLAR_ICONS: Record<Pillar, LucideIcon> = {
  F: Search,
  A: LockOpen,
  I: Link2,
  R: Recycle
};

const STATUS_LABELS: Record<ComplianceGap['status'], string> = {
  exceeds: 'Exceeds',
  on_track: 'On Track',
  gap: 'Gap',
  critical_gap: 'Critical Gap'
};

const ComplianceGapPanel: React.FC = () => {
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPillar, setExpandedPillar] = useState<Pillar | null>(null);

  useEffect(() => {
    fetchComplianceGap();
  }, []);

  const fetchComplianceGap = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/organization/compliance-gap');

      if (!response.ok) {
        throw new Error(`Failed to fetch compliance gap: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Unknown error');
      }

      setReport(data.complianceGap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status: ComplianceGap['status']): string => {
    switch (status) {
      case 'exceeds': return 'compliance-gap__status--exceeds';
      case 'on_track': return 'compliance-gap__status--on-track';
      case 'gap': return 'compliance-gap__status--gap';
      case 'critical_gap': return 'compliance-gap__status--critical';
      default: return '';
    }
  };

  const formatGap = (gap: number | null): string => {
    if (gap === null || gap === undefined || isNaN(gap)) return 'N/A';
    if (gap > 0) return `+${gap.toFixed(1)}`;
    return gap.toFixed(1);
  };

  const toggleExpand = (pillar: Pillar) => {
    setExpandedPillar(expandedPillar === pillar ? null : pillar);
  };

  if (loading) {
    return (
      <div className="compliance-gap-panel compliance-gap-panel--loading">
        <div className="compliance-gap__header">
          <div className="compliance-gap__icon"><BarChart3 size={20} /></div>
          <div className="compliance-gap__title">
            <h4>Policy vs Practice Gap</h4>
            <span className="compliance-gap__subtitle">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="compliance-gap-panel compliance-gap-panel--error">
        <div className="compliance-gap__header">
          <div className="compliance-gap__icon"><BarChart3 size={20} /></div>
          <div className="compliance-gap__title">
            <h4>Policy vs Practice Gap</h4>
            <span className="compliance-gap__subtitle compliance-gap__subtitle--error">
              {error}
            </span>
          </div>
        </div>
        <button onClick={fetchComplianceGap} className="compliance-gap__retry">
          Retry
        </button>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  // Check if we have enough data
  const hasData = report.documentsAnalyzed > 0 && report.datasetsAnalyzed > 0;

  if (!hasData) {
    return (
      <div className="compliance-gap-panel compliance-gap-panel--empty">
        <div className="compliance-gap__header">
          <div className="compliance-gap__icon"><BarChart3 size={20} /></div>
          <div className="compliance-gap__title">
            <h4>Policy vs Practice Gap</h4>
            <span className="compliance-gap__subtitle">
              Insufficient data for analysis
            </span>
          </div>
        </div>
        <div className="compliance-gap__empty-message">
          <p>
            Gap analysis requires both governance documents and dataset analyses.
          </p>
          <ul>
            <li>Documents analyzed: {report.documentsAnalyzed}</li>
            <li>Datasets analyzed: {report.datasetsAnalyzed}</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="compliance-gap-panel">
      {/* Header */}
      <div className="compliance-gap__header">
        <div className="compliance-gap__icon"><BarChart3 size={20} /></div>
        <div className="compliance-gap__title">
          <h4>Policy vs Practice Gap</h4>
          <span className="compliance-gap__subtitle">
            {report.documentsAnalyzed} documents vs {report.datasetsAnalyzed} datasets
          </span>
        </div>
        <div className="compliance-gap__score-badge">
          <span className="compliance-gap__score-value">{report.overallComplianceScore}%</span>
          <span className="compliance-gap__score-label">Compliant</span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="compliance-gap__summary">
        {report.summary.exceeds > 0 && (
          <div className="compliance-gap__stat compliance-gap__stat--exceeds">
            <span className="compliance-gap__stat-value">{report.summary.exceeds}</span>
            <span className="compliance-gap__stat-label">Exceeds</span>
          </div>
        )}
        {report.summary.onTrack > 0 && (
          <div className="compliance-gap__stat compliance-gap__stat--on-track">
            <span className="compliance-gap__stat-value">{report.summary.onTrack}</span>
            <span className="compliance-gap__stat-label">On Track</span>
          </div>
        )}
        {report.summary.gap > 0 && (
          <div className="compliance-gap__stat compliance-gap__stat--gap">
            <span className="compliance-gap__stat-value">{report.summary.gap}</span>
            <span className="compliance-gap__stat-label">Gap</span>
          </div>
        )}
        {report.summary.criticalGap > 0 && (
          <div className="compliance-gap__stat compliance-gap__stat--critical">
            <span className="compliance-gap__stat-value">{report.summary.criticalGap}</span>
            <span className="compliance-gap__stat-label">Critical</span>
          </div>
        )}
      </div>

      {/* FAIR Pillar Gaps */}
      <div className="compliance-gap__pillars">
        {report.gaps.map((gap) => (
          <div
            key={gap.pillar}
            className={`compliance-gap__pillar ${expandedPillar === gap.pillar ? 'compliance-gap__pillar--expanded' : ''}`}
          >
            <div
              className="compliance-gap__pillar-header"
              onClick={() => gap.datasetsBelowBar.length > 0 && toggleExpand(gap.pillar)}
              style={{ cursor: gap.datasetsBelowBar.length > 0 ? 'pointer' : 'default' }}
            >
              <div className="compliance-gap__pillar-icon">
                {React.createElement(PILLAR_ICONS[gap.pillar], { size: 18 })}
              </div>
              <div className="compliance-gap__pillar-info">
                <span className="compliance-gap__pillar-name">{gap.pillarName}</span>
                <div className="compliance-gap__pillar-bars">
                  <div className="compliance-gap__bar-row">
                    <span className="compliance-gap__bar-label">Policy</span>
                    <div className="compliance-gap__bar">
                      <div
                        className="compliance-gap__bar-fill compliance-gap__bar-fill--benchmark"
                        style={{ width: `${(gap.benchmark / 5) * 100}%` }}
                      />
                    </div>
                    <span className="compliance-gap__bar-value">{gap.benchmark.toFixed(1)}</span>
                  </div>
                  <div className="compliance-gap__bar-row">
                    <span className="compliance-gap__bar-label">Practice</span>
                    <div className="compliance-gap__bar">
                      <div
                        className={`compliance-gap__bar-fill compliance-gap__bar-fill--practice ${getStatusClass(gap.status)}`}
                        style={{ width: `${((gap.practice ?? 0) / 5) * 100}%` }}
                      />
                    </div>
                    <span className="compliance-gap__bar-value">{gap.practice !== null ? gap.practice.toFixed(1) : 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div className="compliance-gap__pillar-gap">
                <span className={`compliance-gap__gap-value ${getStatusClass(gap.status)}`}>
                  {formatGap(gap.gap)}
                </span>
                <span className={`compliance-gap__gap-status ${getStatusClass(gap.status)}`}>
                  {STATUS_LABELS[gap.status]}
                </span>
              </div>
              {gap.datasetsBelowBar.length > 0 && (
                <div className="compliance-gap__expand-icon">
                  {expandedPillar === gap.pillar ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              )}
            </div>

            {/* Expanded Drill-down */}
            {expandedPillar === gap.pillar && gap.datasetsBelowBar.length > 0 && (
              <div className="compliance-gap__drilldown">
                <div className="compliance-gap__drilldown-header">
                  <span>Underperforming Datasets ({gap.datasetsBelowBar.length})</span>
                </div>
                <div className="compliance-gap__drilldown-list">
                  {gap.datasetsBelowBar.map((dataset) => (
                    <div key={dataset.journeyId} className="compliance-gap__dataset">
                      <span className="compliance-gap__dataset-name">{dataset.journeyName}</span>
                      <span className="compliance-gap__dataset-score">
                        {dataset.pillarScore.toFixed(1)} / {dataset.benchmarkScore.toFixed(1)}
                      </span>
                      <span className="compliance-gap__dataset-gap compliance-gap__status--critical">
                        {formatGap(dataset.gap)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Timestamp */}
      <div className="compliance-gap__footer">
        <span className="compliance-gap__timestamp">
          Analyzed: {new Date(report.analyzedAt).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default ComplianceGapPanel;
